"use client";

import { useComponentTranslations } from "@/app/components/LanguageProvider";
import { useLocale } from "@/app/components/LocaleProvider";
import { getPathWithLocale } from "@/lib/i18n";
import { useMedia } from "@/hooks/useMedia";
import { MOBILE_QUERY } from "@/hooks/breakpoints";
import Link from "next/link";
import Image from "next/image";

const projects = [
  {
    id: "careers",
    companyKey: "infoCardCareersTitle",
    titleKey: "infoCardCareersDesc",
    buttonKey: "infoCardCareersButton",
    href: "/careers",
    image: "/careers.webp",
  },
  {
    id: "partners",
    companyKey: "infoCardPartnersTitle",
    titleKey: "infoCardPartnersDesc",
    buttonKey: "infoCardPartnersButton",
    href: "/partners",
    image: "/partners.webp",
  },
  {
    id: "referral",
    companyKey: "infoCardReferralTitle",
    titleKey: "infoCardReferralDesc",
    buttonKey: "infoCardReferralButton",
    href: "/referral",
    image: "/referrals.webp",
  },
];

export default function ProjectShowcase() {
  const { t } = useComponentTranslations("InfoSection");
  const locale = useLocale();
  const mobile = useMedia(MOBILE_QUERY);

  const tk = (key: string, fallback?: string) => t(key, fallback) ?? fallback ?? key;

  return (
    <section className={mobile ? "py-9" : "py-16 md:py-24"}>
      <div className="mx-auto max-w-7xl px-4">
        <div className={mobile ? "text-center mb-4" : "text-center mb-12"}>
          <h2 className="text-3xl font-bold tracking-tight text-white/95">
            {tk("sectionTitle")}
          </h2>
          <p className="mt-3 text-sm text-white/70 max-md:mb-4 mb-20 max-w-2xl mx-auto">
            {tk("sectionSubtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 max-md:gap-3 gap-6">
          {projects.map((project) => (
            <Link
              key={project.id}
              href={getPathWithLocale(project.href, locale)}
              className="group block"
            >
              <div className="relative glass-card glass-hover glass-transition overflow-hidden max-md:p-2 p-4 md:p-6 min-h-0 md:min-h-[220px]">
                {mobile ? (
                  <div className="flex gap-2 h-full">
                    <div className="flex-1 flex flex-col justify-center min-w-0">
                      <h3 className="text-sm font-bold text-white mb-1">
                        {tk(project.companyKey)}
                      </h3>
                      <p className="text-xs text-white/60 leading-relaxed line-clamp-1 mb-1">
                        {tk(project.titleKey)}
                      </p>
                      <div className="inline-flex items-center gap-1 text-xs font-semibold text-white/70 group-hover:text-white transition-colors">
                        {tk(project.buttonKey)}
                        <span className="text-xs transition-transform duration-300 group-hover:translate-x-1">→</span>
                      </div>
                    </div>
                    <Image
                      src={project.image}
                      alt={tk(project.companyKey)}
                      width={80}
                      height={80}
                      className="w-[80px] h-[80px] object-cover rounded-xl shrink-0 transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                ) : (
                  <>
                    <div className="absolute top-0 right-0 w-[140px] h-[140px] md:w-[180px] md:h-[180px] overflow-hidden rounded-bl-2xl">
                      <Image
                        src={project.image}
                        alt={tk(project.companyKey)}
                        fill
                        sizes="(max-width: 768px) 140px, 180px"
                        className="object-cover transition-all duration-500 group-hover:scale-110"
                      />
                    </div>
                    <div className="relative z-10 pr-[140px] md:pr-[180px]">
                      <h3 className="text-base md:text-xl font-bold text-white mb-1 md:mb-2">
                        {tk(project.companyKey)}
                      </h3>
                      <p className="text-xs md:text-sm text-white/60 leading-relaxed line-clamp-2 md:line-clamp-3 mb-3 md:mb-4">
                        {tk(project.titleKey)}
                      </p>
                      <div className="inline-flex items-center gap-2 text-xs font-semibold text-white/70 group-hover:text-white transition-colors">
                        {tk(project.buttonKey)}
                        <span className="text-xs transition-transform duration-300 group-hover:translate-x-1">→</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
